import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const waNumber = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (d.length === 10) return `91${d}`;
  if (d.length === 11 && d.startsWith("0")) return `91${d.slice(1)}`;
  return d;
};

const QUICK = [
  ["Follow-up", (c, me) => `Hello ${c.name}, this is ${me} from Thakker Polypack. Just following up on your packaging requirement for ${c.company}. Is this a good time to connect?`],
  ["Requirement details", (c, me) => `Hello ${c.name}, ${me} from Thakker Polypack here. To suggest the right bag, could you share the filling weight per bag, monthly quantity and a photo of your current bag?`],
  ["Quotation reminder", (c, me) => `Hello ${c.name}, ${me} from Thakker Polypack. Did you get a chance to review our quotation? Happy to clarify anything or arrange samples.`],
  ["Sample dispatch", (c, me) => `Hello ${c.name}, ${me} from Thakker Polypack. Your bag samples have been dispatched. Please let me know once received.`],
  ["Meeting reminder", (c, me) => `Hello ${c.name}, ${me} from Thakker Polypack. A quick reminder about our scheduled discussion. Looking forward to speaking with you.`],
];

export default function WhatsAppDialog({ open, onOpenChange, lead, onSent }) {
  const { user } = useAuth();
  const { settings } = useMeta();
  const [msg, setMsg] = useState("");
  const ctx = { name: lead?.contact_person?.split(" ")[0] || "Sir/Madam", company: lead?.company || "" };
  const me = user?.name?.split(" ")[0] || "";
  const quick = [...QUICK];
  if (lead?.stage === "won" && settings?.gmb_review_url) quick.push(["Google review", (c) => `Hello ${c.name}, thank you for choosing Thakker Polypack! If you're happy with our service, we'd be grateful for a quick Google review: ${settings.gmb_review_url}`]);
  useEffect(() => { if (open) setMsg(QUICK[0][1](ctx, me)); }, [open]); // eslint-disable-line
  const send = async () => {
    window.open(`https://wa.me/${waNumber(lead.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
    try { await api.post(`/leads/${lead.lead_id}/whatsapp-log`, { message: msg }); onSent?.(); } catch { /* logging only */ }
    onOpenChange(false);
    toast.success("WhatsApp opened — message logged");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" />WhatsApp {lead?.contact_person || lead?.company}</DialogTitle>
          <DialogDescription>{lead?.phone ? `+${waNumber(lead.phone)} · opens in your own WhatsApp` : "No phone number on this lead"}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {quick.map(([l, fn]) => <button key={l} onClick={() => setMsg(fn(ctx, me))} data-testid={`wa-template-${l.toLowerCase().replace(/\s+/g, "-")}`} className="text-xs rounded-full px-2.5 py-1 ring-1 ring-gray-200 hover:bg-[#ECF5F1] hover:ring-[#A3D9C9]">{l}</button>)}
        </div>
        <Textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="wa-message-input" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!lead?.phone || !msg.trim()} onClick={send} className="bg-[#128C7E] hover:bg-[#0e7368]" data-testid="wa-send-button"><MessageCircle className="h-4 w-4 mr-1.5" />Open WhatsApp</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
